import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../services/api';
import colors from '../../styles/colors';

const SUPPORT_EMAIL = 'jeff@visibilitylabs.ai';

const styles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: colors.bg.offWhite,
  },
  header: {
    background: colors.bg.white,
    padding: '16px 0',
    borderBottom: `1px solid ${colors.border.lightNavy}`,
  },
  headerInner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoImg: { height: '34px', width: 'auto' },
  main: { flex: 1, padding: '48px 0' },
  mainInner: { maxWidth: '980px', margin: '0 auto', padding: '0 24px' },
  title: {
    fontSize: '28px',
    fontWeight: '800',
    color: colors.text.darkNavy,
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: colors.text.muted,
    marginBottom: '18px',
    lineHeight: '1.7',
  },
  toolbar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    borderRadius: '999px',
    border: `1px solid ${colors.border.lightNavy}`,
    background: colors.bg.white,
    fontSize: '13px',
    fontWeight: '800',
    color: colors.text.darkNavy,
  },
  card: {
    background: colors.bg.white,
    border: `1px solid ${colors.border.lightNavy}`,
    borderRadius: '12px',
    padding: '18px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  columnHeader: {
    display: 'grid',
    gridTemplateColumns: '70px 1.6fr 1fr 1fr 48px',
    gap: '14px',
    padding: '0 0 10px',
    borderBottom: `2px solid ${colors.border.light}`,
    alignItems: 'center',
  },
  columnHeaderText: {
    fontSize: '12px',
    fontWeight: '800',
    color: colors.text.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  topicRow: {
    display: 'grid',
    gridTemplateColumns: '70px 1.6fr 1fr 1fr 48px',
    gap: '14px',
    padding: '14px 0',
    borderBottom: `1px solid ${colors.border.light}`,
    alignItems: 'center',
    background: colors.bg.white,
  },
  topicRowDragging: {
    opacity: 0.45,
  },
  topicRowDragOver: {
    background: colors.bg.lightLavender,
  },
  orderCol: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  dragHandle: {
    cursor: 'grab',
    color: colors.text.gray,
    display: 'inline-flex',
    alignItems: 'center',
    userSelect: 'none',
  },
  index: {
    fontSize: '13px',
    color: colors.text.muted,
    fontWeight: '700',
    minWidth: '18px',
  },
  arrowStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  arrowBtn: {
    width: '22px',
    height: '18px',
    borderRadius: '5px',
    border: `1px solid ${colors.border.lightNavy}`,
    background: colors.bg.white,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    color: colors.text.darkNavy,
  },
  arrowBtnDisabled: {
    opacity: 0.35,
    cursor: 'not-allowed',
  },
  titleInput: {
    width: '100%',
    minHeight: '40px',
    borderRadius: '10px',
    border: `1px solid ${colors.border.light}`,
    padding: '9px 12px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  },
  titleInputEdited: {
    border: `1px solid rgba(122,101,253,0.45)`,
    background: 'rgba(122,101,253,0.04)',
  },
  metaCell: {
    fontSize: '13px',
    color: colors.text.darkNavy,
    fontWeight: '600',
  },
  badge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '999px',
    background: colors.bg.lightLavender,
    color: colors.text.accentBlue,
    fontSize: '12px',
    fontWeight: '700',
  },
  editedTag: {
    display: 'inline-block',
    marginTop: '6px',
    fontSize: '12px',
    fontWeight: '800',
    color: colors.text.purple,
  },
  actionsCol: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  deleteBtn: {
    width: '38px',
    height: '38px',
    borderRadius: '999px',
    border: `1px solid ${colors.border.lightNavy}`,
    background: colors.bg.white,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    cursor: 'pointer',
    padding: 0,
  },
  deleteBtnDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
  },
  removedSection: {
    marginTop: '22px',
    paddingTop: '16px',
    borderTop: `1px dashed ${colors.border.light}`,
  },
  removedTitle: {
    fontSize: '13px',
    fontWeight: '800',
    color: colors.text.danger,
    marginBottom: '10px',
  },
  removedRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '10px',
    background: 'rgba(185,28,28,0.04)',
    border: '1px solid rgba(185,28,28,0.14)',
    marginBottom: '8px',
  },
  removedText: {
    fontSize: '14px',
    color: colors.text.muted,
    textDecoration: 'line-through',
  },
  undoBtn: {
    padding: '7px 12px',
    borderRadius: '8px',
    border: `1px solid ${colors.border.lightNavy}`,
    background: colors.bg.white,
    fontSize: '12px',
    fontWeight: '700',
    color: colors.text.darkNavy,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  commentsLabel: {
    marginTop: '18px',
    display: 'block',
    fontSize: '13px',
    fontWeight: '800',
    color: colors.text.darkNavy,
    marginBottom: '8px',
  },
  commentsBox: {
    width: '100%',
    minHeight: '120px',
    resize: 'vertical',
    borderRadius: '10px',
    border: `1px solid ${colors.border.light}`,
    padding: '12px 14px',
    fontSize: '14px',
    fontFamily: 'inherit',
    lineHeight: '1.6',
    outline: 'none',
    boxSizing: 'border-box',
  },
  actions: { marginTop: '18px' },
  button: {
    background: colors.bg.primaryPurple,
    color: colors.text.white,
    border: 'none',
    borderRadius: '10px',
    padding: '12px 18px',
    fontSize: '14px',
    fontWeight: '800',
    cursor: 'pointer',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  errorBanner: {
    background: 'rgba(220, 38, 38, 0.08)',
    border: '1px solid rgba(220, 38, 38, 0.25)',
    color: '#7f1d1d',
    borderRadius: '12px',
    padding: '14px 16px',
    marginBottom: '18px',
    fontSize: '14px',
    fontWeight: '700',
  },
  thankYouWrap: {
    background: `linear-gradient(135deg, ${colors.bg.primaryPurple} 0%, ${colors.bg.darkNavy} 100%)`,
    borderRadius: '16px',
    padding: '28px',
    marginBottom: '24px',
    color: colors.text.white,
    textAlign: 'center',
    boxShadow: '0 12px 30px rgba(23, 15, 73, 0.18)',
  },
  thankYouIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '18px',
    background: 'rgba(255, 255, 255, 0.14)',
    border: '1px solid rgba(255, 255, 255, 0.22)',
    margin: '0 auto 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '30px',
  },
  thankYouTitle: {
    fontSize: '28px',
    fontWeight: '800',
    marginBottom: '10px',
    letterSpacing: '-0.5px',
  },
  thankYouText: {
    maxWidth: '620px',
    margin: '0 auto',
    fontSize: '15px',
    lineHeight: '1.7',
    color: 'rgba(255,255,255,0.86)',
  },
  footer: {
    background: colors.bg.darkNavy,
    padding: '22px 0',
    textAlign: 'center',
  },
  footerText: { fontSize: '13px', color: 'rgba(255,255,255,0.5)' },
};

function normalizeTopic(t) {
  return {
    nocodb_id: Number(t.nocodb_id),
    article_title: typeof t.article_title === 'string' ? t.article_title.replaceAll('\\n', '\n') : (t.article_title || ''),
    content_format: t.content_format || '',
    buyer_journey: t.buyer_journey || '',
  };
}

function ContentStrategyReview() {
  const { uuid } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [locked, setLocked] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const [clientName, setClientName] = useState('');
  const [formTitle, setFormTitle] = useState('Content Strategy Review');
  const [reviewer, setReviewer] = useState('Client');
  const [originalById, setOriginalById] = useState(() => new Map());
  const [topics, setTopics] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [comments, setComments] = useState('');

  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const detailUrl = useMemo(() => `/forms/content-strategy-review/${uuid}/`, [uuid]);

  useEffect(() => {
    setLoading(true);
    setError('');
    setNotFound(false);

    api
      .get(detailUrl)
      .then((res) => {
        const data = res.data || {};
        const isLocked = Boolean(data?.is_locked);
        setLocked(isLocked);
        setShowThankYou(isLocked);
        setClientName(data?.client_name || '');
        setFormTitle(data?.form_title || 'Content Strategy Review');
        setReviewer(data?.reviewer || 'Client');

        const orig = (data?.topics_original || []).map(normalizeTopic);
        const working = (data?.topics_working || []).map(normalizeTopic);
        setOriginalById(new Map(orig.map((t) => [t.nocodb_id, t.article_title])));

        const base = working.length ? working : orig;
        const deletedIds = new Set((data?.deleted_topic_ids || []).map((x) => Number(x)));
        // Active = working list (already excludes deleted on a locked/resubmitted session)
        setTopics(base.filter((t) => !deletedIds.has(t.nocodb_id)));
        // Removed = originals whose id is in deleted set and not present in working
        const workingIds = new Set(base.map((t) => t.nocodb_id));
        setRemoved(orig.filter((t) => deletedIds.has(t.nocodb_id) && !workingIds.has(t.nocodb_id)));
        setComments((data?.additional_comments || '').replaceAll('\\n', '\n'));
        setLoading(false);
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail || 'This form link is invalid.';
        setError(msg);
        setNotFound(err?.response?.status === 404);
        setLoading(false);
      });
  }, [detailUrl]);

  const editedCount = useMemo(() => {
    let count = 0;
    for (const t of topics) {
      const base = originalById.get(t.nocodb_id) ?? '';
      if ((t.article_title || '') !== base) count += 1;
    }
    return count;
  }, [topics, originalById]);

  const updateTitle = (id, text) => {
    setTopics((prev) => prev.map((t) => (t.nocodb_id === id ? { ...t, article_title: text } : t)));
  };

  const deleteTopic = (id) => {
    if (locked) return;
    const target = topics.find((t) => t.nocodb_id === id);
    if (!target) return;
    setTopics((prev) => prev.filter((t) => t.nocodb_id !== id));
    setRemoved((prev) =>
      prev.some((t) => t.nocodb_id === id) ? prev : [...prev, target]
    );
  };

  const undoDelete = (id) => {
    if (locked) return;
    const target = removed.find((t) => t.nocodb_id === id);
    if (!target) return;
    setRemoved((prev) => prev.filter((t) => t.nocodb_id !== id));
    setTopics((prev) =>
      prev.some((t) => t.nocodb_id === id) ? prev : [...prev, target]
    );
  };

  const moveTopic = (from, to) => {
    if (locked) return;
    if (to < 0 || to >= topics.length) return;
    setTopics((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const onDragStart = (idx) => {
    if (locked) return;
    setDragIndex(idx);
  };

  const onDragOver = (e, idx) => {
    if (locked || dragIndex === null) return;
    e.preventDefault();
    setDragOverIndex(idx);
  };

  const onDrop = (idx) => {
    if (locked || dragIndex === null) return;
    moveTopic(dragIndex, idx);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const onDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (locked || submitting) return;

    setSubmitting(true);
    setError('');

    api
      .post(`${detailUrl}submit/`, {
        content_topics: topics.map((t) => ({
          nocodb_id: t.nocodb_id,
          article_title: t.article_title,
          content_format: t.content_format,
          buyer_journey: t.buyer_journey,
        })),
        deleted_topic_ids: removed.map((t) => t.nocodb_id),
        additional_comments: comments,
      })
      .then(() => {
        setLocked(true);
        setShowThankYou(true);
        setSubmitting(false);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.detail ||
          'Could not submit the form. Please try again or contact support.';
        setError(msg);
        setSubmitting(false);
      });
  };

  const heading = clientName ? `${clientName} - ${formTitle}` : formTitle;

  return (
    <div style={styles.wrapper}>
      <style>{`
        @media (max-width: 720px) {
          .csr-col-header { display: none !important; }
          .csr-row {
            grid-template-columns: 1fr !important;
            gap: 8px !important;
          }
          .csr-order-col { flex-direction: row !important; }
          .csr-actions-col { justify-content: flex-start !important; }
        }
      `}</style>

      <header style={styles.header}>
        <div style={styles.headerInner}>
          <a href="https://visibilitylabs.ai" aria-label="Go to visibilitylabs.ai">
            <img src="/visibilitylabs.svg" alt="Visibility Labs" style={styles.logoImg} />
          </a>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.mainInner}>
          {loading ? (
            <div style={styles.card}>Loading form…</div>
          ) : (
            <>
              {notFound && (
                <div style={styles.errorBanner}>
                  Link not found. If you believe this is an error, email{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: colors.text.accentBlue, fontWeight: 800 }}>
                    {SUPPORT_EMAIL}
                  </a>.
                </div>
              )}

              {showThankYou && !notFound && (
                <div style={styles.thankYouWrap}>
                  <div style={styles.thankYouIcon}>✓</div>
                  <div style={styles.thankYouTitle}>Thank you!</div>
                  <div style={styles.thankYouText}>
                    Thanks for reviewing and approving the content strategy for{' '}
                    {clientName || 'your company'}.
                    <br />
                    We’ve received your feedback and will take it from here.
                    <br />
                    Full speed ahead!
                  </div>
                </div>
              )}

              {!notFound && (
                <>
                  <h1 style={styles.title}>{heading}</h1>
                  <p style={styles.subtitle}>
                    Please review the suggested article topics below.
                    <br />
                    You can edit the article titles directly, delete any rows you don’t want, and drag (or use the
                    arrows) to re-order the list.
                    <br />
                    When everything looks right, click{' '}
                    <b>{reviewer === 'PM' ? 'Approve & Send to Client' : 'Approve & Submit'}</b> at the bottom.
                  </p>

                  {error && <div style={styles.errorBanner}>{error}</div>}

                  <div style={styles.toolbar}>
                    <div style={styles.pill}>Articles: {topics.length}</div>
                    <div style={styles.pill}>Edited: {editedCount}</div>
                    <div style={styles.pill}>Removed: {removed.length}</div>
                  </div>

                  <form onSubmit={onSubmit} style={styles.card}>
                    <div style={styles.columnHeader} className="csr-col-header">
                      <div style={styles.columnHeaderText}>Order</div>
                      <div style={styles.columnHeaderText}>Article Topic</div>
                      <div style={styles.columnHeaderText}>Content Format</div>
                      <div style={styles.columnHeaderText}>Buyers Journey</div>
                      <div />
                    </div>

                    {topics.map((t, idx) => {
                      const original = originalById.get(t.nocodb_id) ?? '';
                      const isEdited = (t.article_title || '') !== original;
                      return (
                        <div
                          key={t.nocodb_id}
                          className="csr-row"
                          style={{
                            ...styles.topicRow,
                            ...(dragIndex === idx ? styles.topicRowDragging : {}),
                            ...(dragOverIndex === idx && dragIndex !== idx ? styles.topicRowDragOver : {}),
                          }}
                          draggable={!locked}
                          onDragStart={() => onDragStart(idx)}
                          onDragOver={(e) => onDragOver(e, idx)}
                          onDrop={() => onDrop(idx)}
                          onDragEnd={onDragEnd}
                        >
                          <div style={styles.orderCol} className="csr-order-col">
                            <span style={styles.dragHandle} title="Drag to reorder" aria-hidden="true">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <circle cx="9" cy="6" r="1.6" />
                                <circle cx="15" cy="6" r="1.6" />
                                <circle cx="9" cy="12" r="1.6" />
                                <circle cx="15" cy="12" r="1.6" />
                                <circle cx="9" cy="18" r="1.6" />
                                <circle cx="15" cy="18" r="1.6" />
                              </svg>
                            </span>
                            <span style={styles.index}>{idx + 1}</span>
                            <span style={styles.arrowStack}>
                              <button
                                type="button"
                                title="Move up"
                                aria-label="Move up"
                                onClick={() => moveTopic(idx, idx - 1)}
                                disabled={locked || idx === 0}
                                style={{
                                  ...styles.arrowBtn,
                                  ...(locked || idx === 0 ? styles.arrowBtnDisabled : {}),
                                }}
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="18 15 12 9 6 15" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                title="Move down"
                                aria-label="Move down"
                                onClick={() => moveTopic(idx, idx + 1)}
                                disabled={locked || idx === topics.length - 1}
                                style={{
                                  ...styles.arrowBtn,
                                  ...(locked || idx === topics.length - 1 ? styles.arrowBtnDisabled : {}),
                                }}
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="6 9 12 15 18 9" />
                                </svg>
                              </button>
                            </span>
                          </div>

                          <div>
                            <input
                              style={{
                                ...styles.titleInput,
                                ...(isEdited ? styles.titleInputEdited : {}),
                              }}
                              value={t.article_title || ''}
                              disabled={locked}
                              onChange={(e) => updateTitle(t.nocodb_id, e.target.value)}
                            />
                            {isEdited && <div style={styles.editedTag}>Edited</div>}
                          </div>

                          <div style={styles.metaCell}>
                            {t.content_format ? <span style={styles.badge}>{t.content_format}</span> : '—'}
                          </div>
                          <div style={styles.metaCell}>
                            {t.buyer_journey ? <span style={styles.badge}>{t.buyer_journey}</span> : '—'}
                          </div>

                          <div style={styles.actionsCol} className="csr-actions-col">
                            <button
                              type="button"
                              title="Delete row"
                              aria-label="Delete row"
                              onClick={() => deleteTopic(t.nocodb_id)}
                              disabled={locked}
                              style={{
                                ...styles.deleteBtn,
                                ...(locked ? styles.deleteBtnDisabled : {}),
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.text.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                <path d="M10 11v6M14 11v6" />
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {removed.length > 0 && (
                      <div style={styles.removedSection}>
                        <div style={styles.removedTitle}>Removed articles ({removed.length})</div>
                        {removed.map((t) => (
                          <div key={t.nocodb_id} style={styles.removedRow}>
                            <span style={styles.removedText}>{t.article_title || '—'}</span>
                            {!locked && (
                              <button type="button" style={styles.undoBtn} onClick={() => undoDelete(t.nocodb_id)}>
                                Undo
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <label style={styles.commentsLabel}>
                      Additional Comments{' '}
                      <span style={{ color: colors.text.muted, fontWeight: 400 }}>(optional)</span>
                    </label>
                    <textarea
                      style={styles.commentsBox}
                      value={comments}
                      disabled={locked}
                      onChange={(e) => setComments(e.target.value)}
                      placeholder="Any notes about the topics, formats, or anything you’d like to tell us…"
                    />

                    <div style={styles.actions}>
                      {!locked && (
                        <>
                          <button
                            type="submit"
                            style={{ ...styles.button, ...(submitting ? styles.buttonDisabled : {}) }}
                            disabled={submitting}
                          >
                            {submitting
                              ? 'Submitting…'
                              : reviewer === 'PM'
                              ? 'Approve & Send to Client'
                              : 'Approve & Submit'}
                          </button>
                          {reviewer === 'PM' && (
                            <p style={{ marginTop: '10px', fontSize: '13px', color: colors.text.muted, lineHeight: '1.6' }}>
                              By clicking this button your changes (edits, deletions, and re-ordering) will be recorded
                              and sent to the client automatically.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </form>
                </>
              )}
            </>
          )}
        </div>
      </main>

      <footer style={styles.footer}>
        <p style={styles.footerText}>
          Visibility Labs &copy; {new Date().getFullYear()}. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default ContentStrategyReview;
